import { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import { Header } from "@/components/Header";
import client from "@/api";
import { useNavigate } from "react-router-dom";
import { PlayCircle, GraduationCap, TrendingUp } from "lucide-react";

export default function Dashboard() {
  const { t, lang, user } = useApp();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    client.get(`/dashboard?lang=${lang}`).then(({ data }) => setData(data)).catch(() => setErr(true));
  }, [lang]);

  if (err) return <Shell><p className="text-[#C9CDD3]" data-testid="dash-error">{t("error")}</p></Shell>;
  if (!data) return <Shell><Skeleton /></Shell>;

  const pct = data.total_chapters ? Math.round((data.completed_chapters / data.total_chapters) * 100) : 0;

  return (
    <Shell>
      <div className="fade-up">
        <h1 className="font-serif text-3xl sm:text-4xl text-[#FFFFFF]">{t("dash_welcome")}, {user?.name?.split(" ")[0]}</h1>
      </div>
      <div className="grid lg:grid-cols-3 gap-5 mt-8">
        {/* Progress */}
        <div className="card-dark rounded-2xl p-6 flex items-center gap-6" data-testid="progress-card">
          <ProgressRing pct={pct} />
          <div>
            <div className="text-xs uppercase tracking-wider text-[#D21F2A]">{t("dash_progress")}</div>
            <div className="font-serif text-2xl text-[#FFFFFF] mt-1">{data.completed_chapters} / {data.total_chapters}</div>
            <div className="text-sm text-[#C9CDD3]">{t("dash_completed")}</div>
          </div>
        </div>

        {/* Resume */}
        <button data-testid="resume-btn" onClick={() => navigate(`/chapters/${data.resume_chapter_id}`)}
          className="card-dark rounded-2xl p-6 text-left hover:border-[#D21F2A]/40 transition-colors group">
          <PlayCircle className="w-8 h-8 text-[#D21F2A] mb-3 group-hover:scale-110 transition-transform" />
          <div className="font-serif text-xl text-[#FFFFFF]">{t("dash_resume")}</div>
          <div className="text-sm text-[#C9CDD3] mt-1">→ {data.resume_chapter_id}</div>
        </button>

        {/* Final */}
        <button data-testid="final-card" onClick={() => navigate("/final")}
          className="card-dark rounded-2xl p-6 text-left hover:border-[#D21F2A]/40 transition-colors">
          <GraduationCap className="w-8 h-8 text-[#D21F2A] mb-3" />
          <div className="font-serif text-xl text-[#FFFFFF]">{t("dash_final_card")}</div>
          <div className="text-sm text-[#C9CDD3] mt-1">
            {data.final_eligible ? t("dash_final_ready") : `${data.final_missing_count} ${t("dash_final_missing")}`}
          </div>
        </button>
      </div>

      {/* Recent attempts */}
      <div className="mt-10">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-5 h-5 text-[#D21F2A]" />
          <h2 className="font-serif text-2xl text-[#FFFFFF]">{t("dash_recent")}</h2>
        </div>
        {data.recent_attempts.length === 0 ? (
          <p className="text-[#9AA0A6] text-sm" data-testid="no-attempts">{t("dash_no_attempts")}</p>
        ) : (
          <div className="space-y-2" data-testid="recent-attempts">
            {data.recent_attempts.map((a, i) => (
              <div key={i} className="flex items-center justify-between px-4 py-3 rounded-xl bg-[#141414] border border-[#D21F2A]/10">
                <span className="text-[#FFFFFF] text-sm">{a.chapter_title}</span>
                <div className="flex items-center gap-4">
                  <span className="text-xs text-[#9AA0A6]">{new Date(a.submitted_at).toLocaleDateString()}</span>
                  <span className="font-serif text-lg text-[#D21F2A]">{a.percent}%</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#0d0d0d] bg-grain">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-8 py-10">{children}</main>
    </div>
  );
}

function ProgressRing({ pct }) {
  const r = 34, c = 2 * Math.PI * r;
  return (
    <svg width="88" height="88" viewBox="0 0 88 88" className="shrink-0">
      <circle cx="44" cy="44" r={r} fill="none" stroke="#3a2c20" strokeWidth="8" />
      <circle cx="44" cy="44" r={r} fill="none" stroke="#D21F2A" strokeWidth="8" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={c - (c * pct) / 100} transform="rotate(-90 44 44)"
        style={{ transition: "stroke-dashoffset .6s ease" }} />
      <text x="44" y="50" textAnchor="middle" fill="#FFFFFF" fontSize="18" fontFamily="Playfair Display">{pct}%</text>
    </svg>
  );
}

function Skeleton() {
  return <div className="space-y-4 animate-pulse">
    <div className="h-10 w-64 bg-[#1a1a1a] rounded" />
    <div className="grid lg:grid-cols-3 gap-5"><div className="h-32 bg-[#1a1a1a] rounded-2xl" /><div className="h-32 bg-[#1a1a1a] rounded-2xl" /><div className="h-32 bg-[#1a1a1a] rounded-2xl" /></div>
  </div>;
}
